import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  organizationBrandingDtoSchema,
  organizationDtoSchema,
  type OrganizationBrandingDto,
  type OrganizationDto,
} from '@lib/dto/organization.dto'
import type { UpdateOrganizationInput } from '@lib/input/organization.input'
import type { IOrganizationService } from '@/types/OrganizationService.types'

/** Service for organization-wide settings. */
class OrganizationService implements IOrganizationService {
  async get(): Promise<OrganizationDto> {
    try {
      return await api.get('/organization', organizationDtoSchema)
    } catch (error) {
      logger.error('Failed to load organization settings', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * The name and logo, readable with no account.
   *
   * Its own endpoint rather than `get()` because the screens that need it —
   * login, invite-accept, a client reading a shared proposal — have no
   * session, and because the rest of the settings are nobody else's business.
   */
  async getBranding(): Promise<OrganizationBrandingDto> {
    try {
      return await api.get("/organization/branding", organizationBrandingDtoSchema)
    } catch (error) {
      logger.error("Failed to load organization branding", {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(input: UpdateOrganizationInput): Promise<OrganizationDto> {
    try {
      return await api.put('/organization', input, organizationDtoSchema)
    } catch (error) {
      logger.error('Failed to save organization settings', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const organizationService: IOrganizationService =
  new OrganizationService()
